/**
 * AppRoutes.guards.jsx — 진입·리다이렉트 판정 컴포넌트 (2026-09-10 분리)
 *
 * AppRoutes.jsx 가 464줄이라 300줄 규칙에 걸려 분리했다. **코드 이동만이고 동작 변경은 없다.**
 *
 * 여기 있는 것들은 "이 사람을 이 화면에 들여보낼 것인가, 어디로 보낼 것인가" 를 정한다.
 * 회사별 래퍼는 AppRoutes.company.jsx 에 따로 있다.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { apiClient } from './api/apiClient';
import { isReturningVisitor, markReturningVisitor } from './utils/returningVisitor';
import { setLastScreenOwner, takeLastScreen } from './utils/lastScreen';
import LandingPage from './pages/landing/LandingPage';
import { useAppPermissions } from './hooks/useAppPermissions';
import { isAdminUser } from './pages/admin/adminlayout/sidebarFilters';
import SituationBoard from './pages/admin/SituationBoard';
import AdminHome from './pages/admin/home/AdminHome';
import WorkAssignment from './pages/admin/WorkAssignment';
import SaasTenantManagement from './pages/admin/saas/SaasTenantManagement';
import UserManagement from './pages/admin/UserManagement';
import IntegratedMenuManagement from './pages/admin/IntegratedMenuManagement';
import IntegratedHome from './pages/admin/integratedhome/IntegratedHome';
import { useCompanyScope } from './pages/admin/adminlayout/CompanyScopeContext';

// 첫 주소에 소개 페이지를 그 자리에서 보여 줄 때 쓰는 표시(주소가 아니다).
const SHOW_LANDING = '__landing__';

export function HomeRedirect() {
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState('/login');
  const { pathname } = useLocation();
  const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 768;

  useEffect(() => {
    let active = true;
    (async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        // 소개 페이지(2026-09-28): 이 기기에서 로그인한 적 없고 사이트 스위치가 켜져 있을 때만.
        //   로그인한 적 있음·스위치 꺼짐(파인그룹)·조회 실패 = 종전처럼 로그인 화면.
        let landing = false;
        if (!isReturningVisitor()) {
          try {
            const info = await apiClient.get('/api/site-info');
            landing = info.ok && info.data?.data?.landing === true;
          } catch (_) {
            landing = false; // 인터넷 끊김 등으로 요청 자체가 실패 — "로딩 중"에 멈추지 않고 로그인 화면
          }
        }
        if (active) {
          setTarget(landing ? SHOW_LANDING : '/login');
          setLoading(false);
        }
        return;
      }

      // 토큰은 로그인해야만 생긴다 → 만료됐어도 고객 기기. /api/me 전에 남겨야 만료로 지워져도 기억이 남는다.
      markReturningVisitor();
      const me = await apiClient.get('/api/me');
      if (!active) return;

      if (!me.ok) {
        localStorage.removeItem('accessToken');
        setTarget('/login');
        setLoading(false);
        return;
      }

      const user = me.data?.user || {};
      const role = String(user.role || '');
      // 마지막 작업 화면(2026-10-26, utils/lastScreen) — 이 기기·이 계정이 마지막으로 본 왼쪽 메뉴 화면이 있으면 그 화면.
      setLastScreenOwner(user.userId);
      const lastScreen = takeLastScreen(user.userId);
      // 테넌트 관리자: 메인 회사 일반 직원관리(테넌트 스코프). '테넌트 관리'는 운영자 전용.
      // 2026-08-26 A안: 데스크톱 관리자·테넌트 관리자 랜딩 = 홈(기능 카드 런처).
      //   모바일 관리자는 기존 습관(미팅지도) 유지. 일반 직원은 기존 그대로.
      setTarget(lastScreen || (
        role === 'admin'
          ? (isMobileViewport ? '/admin/meeting-map' : '/admin/home')
          : (user.isTenantAdmin ? '/admin/home' : '/dashboard')
      ));
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [isMobileViewport]);

  if (loading) return <div style={{ padding: 24 }}>로딩 중...</div>;
  if (target === SHOW_LANDING) {
    // 모르는 주소(*)로 온 처음 방문자는 첫 주소로 옮겨서 소개 페이지 — 주소창이 meetinglink.kr 로 보이게.
    return pathname === '/' ? <LandingPage /> : <Navigate to="/" replace />;
  }
  return <Navigate to={target} replace />;
}

// /admin/board 진입 시 admin 아닌 사용자(일반 직원/위장접속) 는 /my/board 로 자동 redirect.
// 위장접속·직원 입장에서 관리자 페이지가 보이지 않게 강제.
export function AdminBoardGuard() {
  if (!isAdminUser()) return <Navigate to="/my/board" replace />;
  return <SituationBoard />;
}

// /admin/home — 홈(기능 카드 런처). 운영자(role=admin)=항상, 테넌트 관리자=HOME 모듈 부여 시만 (2026-08-26).
//   테넌트 직원은 직원 레이아웃(/my)이라 진입 자체가 없고, 미부여 테넌트 관리자는 직원 관리로 보냄.
export function AdminHomeRoute() {
  const { permCodes, loaded } = useAppPermissions();
  if (isAdminUser()) return <AdminHome />;
  if (!loaded) return <div style={{ padding: 24 }}>로딩 중...</div>;
  return (permCodes && permCodes.has('HOME')) ? <AdminHome /> : <Navigate to="/admin/users" replace />;
}

// /my/ index — SITUATION_BOARD 권한 있으면 board, 없으면 attendance 로 분기.
// (직원이 재방문 시에도 상황판이 첫 페이지가 되도록 — 2026-06-08)
export function MyIndexRedirect() {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { apiClient } = await import('./api/apiClient');
        const res = await apiClient.get('/api/my/permissions');
        const codes = res?.ok && Array.isArray(res.data?.permCodes) ? res.data.permCodes : [];
        setTarget(codes.includes('SITUATION_BOARD') ? 'board' : 'attendance');
      } catch (_) {
        setTarget('attendance');
      }
    })();
  }, []);
  if (!target) return <div style={{ padding: 24 }}>로딩 중...</div>;
  return <Navigate to={target} replace />;
}

// imp_token URL 파라미터 → sessionStorage 글로벌 처리.
// 위장접속 새 탭이 어느 경로(/dashboard, /my/board 등)로 열리든 imp_token 자동 인식.
export function ImpTokenInitializer() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get('imp_token');
    if (impToken) {
      import('./api/apiClient').then(({ apiClient }) => {
        apiClient.initImpersonation(impToken);
        params.delete('imp_token');
        const newSearch = params.toString();
        window.history.replaceState(null, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
        // imp_token 적용 후 페이지 강제 reload 로 토큰 적용된 상태로 데이터 fetch
        window.location.reload();
      });
    }
  }, []);
  return null;
}

export function WorkAssignmentRoute() {
  // 업무/기능 배정 = 부여받아야 쓰는 메뉴. 운영자(admin)는 항상 전체 화면. 그 외는 WORK_ASSIGNMENT 보유 필요:
  //   테넌트 관리자(TENANT_ADMIN+WORK_ASSIGNMENT)=전체(스코프) 화면 / 일반 위임자(WORK_ASSIGNMENT만)=/my(위임자).
  //   부여 안 받은 사용자가 직접 URL 진입 시 홈으로.
  const [state, setState] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.all([apiClient.get('/api/me'), apiClient.get('/api/my/permissions')])
      .then(([meRes, permRes]) => {
        if (!active) return;
        const role = meRes.ok ? String(meRes.data?.user?.role || '') : '';
        const codes = permRes.ok && Array.isArray(permRes.data?.permCodes) ? permRes.data.permCodes : [];
        setState({ role, isTenantAdmin: codes.includes('TENANT_ADMIN'), hasWorkAssignment: codes.includes('WORK_ASSIGNMENT') });
      })
      .catch(() => { if (active) setState({ role: '', isTenantAdmin: false, hasWorkAssignment: false }); });
    return () => { active = false; };
  }, []);

  if (state === null) return <div style={{ padding: 24 }}>로딩 중...</div>;
  if (state.role === 'admin') return <WorkAssignment />;
  if (!state.hasWorkAssignment) return <Navigate to="/" replace />;
  if (state.isTenantAdmin) return <WorkAssignment />;
  return <Navigate to="/my/work-feature-assignments" replace />;
}

// '테넌트 관리'는 운영사(role='admin') 전용. 테넌트 관리자가 직접 URL 진입 시 자기 홈으로.
export function SaasTenantsRoute() {
  if (!isAdminUser()) return <Navigate to="/" replace />;
  return <SaasTenantManagement />;
}

// 통합 메뉴 관리 (2026-10-01 운영사 전용 → 2026-10-15 '통합 메뉴 관리' 열쇠 보유자도 — 테넌트 관리자 자동).
//   열쇠 보유자는 서버가 자기 테넌트 메뉴만 준다. 열쇠를 아직 모르면 기다리고, 없으면 첫 화면으로(fail-closed).
export function IntegratedMenusRoute() {
  const scope = useCompanyScope();
  if (isAdminUser()) return <IntegratedMenuManagement />;
  if (!scope) return <Navigate to="/" replace />;
  if (!scope.manageKeyReady) return <div style={{ padding: 24 }}>로딩 중...</div>;
  if (!scope.canManageIntegrated) return <Navigate to="/" replace />;
  return <IntegratedMenuManagement />;
}

// /admin/integrated — 통합 관리 홈 (2026-10-01). 통합관리를 쓸 수 있는 사람만 — 회사 칸의 "통합관리"와 같은 값.
//   목록과 (운영자) 고른 테넌트 회사가 도착할 때까지 기다린다: 새로고침·테넌트 전환 중에 잘못 튕기지 않게.
//   못 쓰면 운영자·HOME 열쇠는 홈, 그 외는 각자의 첫 화면('/').
export function IntegratedHomeRoute() {
  const scope = useCompanyScope();
  if (!scope) return <Navigate to="/" replace />;
  if (!scope.integratedReady) return <div style={{ padding: 24 }}>로딩 중...</div>;
  if (scope.canUseIntegrated) return <IntegratedHome />;
  return <Navigate to={scope.canSeeHome ? '/admin/home' : '/'} replace />;
}

// 직원 관리: 뷰어의 회사로 스코프(운영자=사이트 본사 / 테넌트 관리자=자기 회사), 회사를 못 찾으면 이 사이트 본사(hqCompanyCode, 2026-10-30). companyCode 를 명시 전달하지 않으면
//   UserManagement 가 'PINE' 기본값을 써서 부서·근무유형 드롭다운이 PINE 데이터로 뜨던 누출을 막는다.
export function UsersRoute() {
  const [companyCode, setCompanyCode] = useState(null);
  useEffect(() => {
    let active = true;
    apiClient.get('/api/me')
      .then((res) => {
        if (!active) return;
        const u = res.ok ? res.data?.user : null;
        setCompanyCode(u?.company?.code || u?.hqCompanyCode || 'PINE');
      })
      .catch(() => { if (active) setCompanyCode('PINE'); });
    return () => { active = false; };
  }, []);
  if (companyCode === null) return <div style={{ padding: 24 }}>로딩 중...</div>;
  return <UserManagement companyCode={companyCode} />;
}

// 출퇴근 관리 입구(옛 AttendanceRoute)는 [달력][직원별 상세] 탭 화면으로 옮겼다(2026-09-29) —
//   pages/admin/attendancehub/AttendanceHubRoutes.jsx HqAttendanceHub. 회사값 규칙(뷰어 회사, 모르면 PINE)은 그대로.
